import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HeartHandshake, ShieldCheck, Smile, Truck, Lock, Mail, Plus } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { FloatingShapes } from '@/components/health/FloatingShapes';
import { PHARMACIST_IMAGE, HEALTH_CATEGORIES } from '@/lib/health';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay },
});

const VALUES = [
  { icon: ShieldCheck, title: 'Genuine, always', desc: 'Every item is sealed, in date and sourced from trusted suppliers.', color: 'bg-health-mint text-health-emerald' },
  { icon: Smile, title: 'Friendly & clear', desc: 'Plain-language product info so you know exactly what you are buying.', color: 'bg-health-peach text-health-coral' },
  { icon: HeartHandshake, title: 'Care first', desc: 'We are real people who reply to every message with care.', color: 'bg-health-sky text-sky-700' },
];

const PERKS = [
  { icon: Truck, title: 'Tracked delivery', desc: 'Follow your parcel with email updates from dispatch to doorstep.' },
  { icon: Lock, title: 'Private & discreet', desc: 'Plain packaging and secure checkout keep your health your business.' },
];

const AboutUsPage = () => (
  <div className="min-h-screen bg-background font-body text-health-ink">
    <Navbar />

    <section className="relative overflow-hidden pt-32 pb-20 bg-health-cream">
      <FloatingShapes className="hidden md:block" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div {...fadeUp()}>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-health-emerald"><Plus className="h-3 w-3" strokeWidth={3} /> About PACTAC Health</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 mb-6" data-testid="about-title">The friendly corner shop for your medicine cabinet</h1>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            PACTAC Health started with a simple idea: buying everyday health essentials should be easy, honest and a little bit cheerful. From a fever at midnight to a grazed knee at the park, we help you stay ready.
          </p>
        </motion.div>
        <motion.div {...fadeUp(0.15)} className="relative">
          <div className="absolute -inset-4 bg-health-mint rounded-[2.5rem] -rotate-2" />
          <img src={PHARMACIST_IMAGE} alt="Our friendly team" className="relative w-full aspect-[4/3] object-cover rounded-[2rem] shadow-lift" />
        </motion.div>
      </div>
    </section>

    <section className="py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.h2 {...fadeUp()} className="font-heading text-3xl md:text-4xl font-bold mb-12">What we stand for</motion.h2>
        <div className="grid md:grid-cols-3 gap-6">
          {VALUES.map((v, i) => (
            <motion.div key={v.title} {...fadeUp(i * 0.1)} whileHover={{ y: -6 }} className="bg-white rounded-[2rem] p-8 border border-health-line shadow-soft" data-testid={`about-value-${i}`}>
              <span className={`w-14 h-14 rounded-2xl ${v.color} flex items-center justify-center mb-6`}><v.icon className="h-7 w-7" /></span>
              <h3 className="font-heading text-xl font-bold mb-2">{v.title}</h3>
              <p className="text-slate-600">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
        <motion.div {...fadeUp()}>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">What you'll find on our shelves</h2>
          <p className="text-slate-600 mb-8">We focus on over-the-counter products only, so anyone can shop with confidence. No prescriptions needed.</p>
          <div className="space-y-4">
            {HEALTH_CATEGORIES.map((c) => (
              <div key={c.slug} className={`flex items-center gap-4 ${c.tint} rounded-2xl p-3`}>
                <img src={c.image} alt="" className="w-16 h-16 rounded-xl object-cover" />
                <div>
                  <p className="font-heading font-bold">{c.name}</p>
                  <p className="text-sm text-slate-600">{c.tagline}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div {...fadeUp(0.15)} className="space-y-6 self-center">
          {PERKS.map((p) => (
            <div key={p.title} className="flex gap-4 bg-background rounded-2xl p-6 border border-health-line">
              <span className="w-12 h-12 rounded-xl bg-health-emerald text-white flex items-center justify-center shrink-0"><p.icon className="h-6 w-6" /></span>
              <div>
                <h3 className="font-heading text-lg font-bold mb-1">{p.title}</h3>
                <p className="text-slate-600">{p.desc}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>

    <section className="py-24">
      <motion.div {...fadeUp()} className="max-w-4xl mx-auto px-6 text-center bg-health-coral text-white rounded-[2.5rem] py-16 relative overflow-hidden">
        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Questions? We'd love to help</h2>
        <p className="text-white/90 mb-8 max-w-lg mx-auto">Drop us a line about an order or a product and our team will get back to you.</p>
        <div className="flex flex-wrap gap-4 justify-center">
          <a href="mailto:hello@pactac.shop" data-testid="about-contact-email">
            <Button className="h-12 px-7 rounded-full bg-white text-health-coral hover:bg-health-peach font-bold"><Mail className="h-4 w-4 mr-2" /> hello@pactac.shop</Button>
          </a>
          <Link to="/products"><Button variant="outline" className="h-12 px-7 rounded-full border-2 border-white bg-transparent text-white hover:bg-white/10 font-bold" data-testid="about-shop-button">Start shopping</Button></Link>
        </div>
      </motion.div>
    </section>

    <Footer />
  </div>
);

export default AboutUsPage;
